import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import { friendlyError } from "@/lib/errors";
import { Clock, ShieldAlert, RotateCcw, LogOut } from "lucide-react";
import { toast } from "sonner";

// Tela de espera (Story 2.3 / FR1). Shown to users whose access_status is not
// "allowed". ProtectedRoute routes here on access gates and bounces back out the
// moment /auth/me reports "allowed" — so we just keep refetching (on focus + a
// manual button) and let the guard do the redirect.
export default function Waiting() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [checking, setChecking] = useState(false);

  const recheck = async () => {
    setChecking(true);
    try {
      const me = await refresh();
      if (me?.access_status === "allowed") toast.success("Acesso liberado! Redirecionando… ✦");
      // Guard bounces on "allowed"; otherwise the click must not feel dead.
      else toast("Ainda em análise — avisamos assim que liberar.");
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    const onFocus = () => refresh().catch(() => {});
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  const blocked = user?.access_status === "blocked";

  return (
    <div
      className="min-h-screen bg-brand-bg flex items-center justify-center px-6 py-12 relative overflow-hidden"
      data-testid="waiting-page"
    >
      <div className="absolute inset-0 starfield opacity-90 pointer-events-none" />
      <div className="absolute inset-0 bloom-top pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <BrandLogo size="lg" />
        </div>
        <div className="glass-card rounded-3xl p-8 sm:p-10 text-center" data-testid={blocked ? "waiting-blocked" : "waiting-pending"}>
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 border ${blocked ? "bg-brand-rose/10 border-brand-rose/40" : "bg-brand-elevated border-brand-line"}`}>
            {blocked ? (
              <ShieldAlert className="w-7 h-7 text-brand-rose" />
            ) : (
              <Clock className="w-7 h-7 text-brand-rose" />
            )}
          </div>

          {blocked ? (
            <>
              <h1 className="font-display text-3xl text-brand-ink mb-2">Acesso bloqueado</h1>
              <p className="text-[14.5px] text-brand-ink2 mb-8">
                Seu acesso está bloqueado no momento. Se acha que foi um engano, fale com o suporte.
              </p>
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl text-brand-ink mb-2">Conta criada!</h1>
              <p className="text-[14.5px] text-brand-ink2 mb-8">
                Seu acesso está em análise. Avisaremos assim que liberar — normalmente é rápido.
              </p>
            </>
          )}

          <button
            type="button"
            data-testid="waiting-recheck"
            onClick={recheck}
            disabled={checking}
            className="w-full inline-flex items-center justify-center gap-2 bg-brand-rose hover:bg-brand-roseHover text-white px-6 py-3.5 rounded-full font-ui text-[14px] tracking-wide transition-colors mb-3 min-h-[44px] disabled:opacity-60"
          >
            <RotateCcw className={`w-4 h-4 ${checking ? "animate-spin" : ""}`} />
            {checking ? "Verificando…" : "Verificar novamente"}
          </button>
          <button
            type="button"
            data-testid="waiting-logout"
            onClick={handleLogout}
            className="w-full inline-flex items-center justify-center gap-2 bg-brand-elevated border border-brand-line hover:border-brand-rose text-brand-ink2 hover:text-brand-ink px-6 py-3 rounded-full font-ui text-[13px] transition-colors min-h-[44px]"
          >
            <LogOut className="w-4 h-4" />
            Sair
          </button>
        </div>
      </div>
    </div>
  );
}
